import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Mail, Shield, Link2, LinkIcon, CheckCircle2, XCircle, Send, ExternalLink, Copy,
} from "lucide-react";
import { toast } from "sonner";

export default function MailSettings() {
  const nav = useNavigate();
  const location = useLocation();
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({
    client_id: "", tenant_id: "", client_secret: "", from_email: "", redirect_uri: "",
  });
  const [busy, setBusy] = useState(false);

  async function load() {
    const { data } = await api.get("/settings/microsoft");
    setSettings(data);
    setForm((f) => ({
      ...f,
      client_id: data.client_id || "",
      tenant_id: data.tenant_id || "",
      from_email: data.from_email || "",
      redirect_uri: data.redirect_uri || "",
      client_secret: data.has_secret ? "" : "",
    }));
  }

  useEffect(() => { load(); }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("connected") === "1") {
      toast.success("Microsoft hesabı başarıyla bağlandı");
      nav("/settings/mail", { replace: true });
      load();
    } else if (params.get("error")) {
      toast.error("Bağlantı hatası: " + params.get("error"));
      nav("/settings/mail", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  async function save() {
    if (!form.client_id || !form.tenant_id || !form.from_email) return toast.error("Client ID, Tenant ID ve gönderici e-posta zorunlu");
    if (!settings?.has_secret && !form.client_secret) return toast.error("Client Secret gerekli");
    setBusy(true);
    try {
      const payload = { ...form };
      if (!payload.client_secret && settings?.has_secret) delete payload.client_secret;
      await api.put("/settings/microsoft", payload);
      toast.success("Ayarlar kaydedildi");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  }

  async function connect() {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/microsoft/authorize");
      window.location.href = data.authorize_url;
    } catch (e) {
      toast.error(formatApiError(e));
      setBusy(false);
    }
  }

  async function disconnect() {
    if (!window.confirm("Microsoft bağlantısı kaldırılsın mı?")) return;
    await api.post("/auth/microsoft/disconnect");
    toast.success("Bağlantı kaldırıldı");
    await load();
  }

  async function testSend() {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/microsoft/test-send");
      toast.success(`Test e-postası gönderildi: ${data.sent_to}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  }

  function copy(text) {
    navigator.clipboard.writeText(text);
    toast.success("Kopyalandı");
  }

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const suggestedRedirect = `${backendUrl}/api/auth/microsoft/callback`;

  return (
    <div className="space-y-6 max-w-4xl" data-testid="mail-settings-page">
      <header>
        <div className="mono text-xs uppercase tracking-[0.3em] text-amber-400">Ayarlar</div>
        <h1 className="text-4xl font-bold tracking-tight mt-1 flex items-center gap-3">
          <Mail className="w-8 h-8 text-amber-400" /> Mail Bağlantısı
        </h1>
        <p className="text-slate-400 mt-1 text-sm">Mailleri kendi kurumsal Microsoft 365 hesabınızdan gönderin. Şifreniz asla saklanmaz.</p>
      </header>

      {/* Status card */}
      <div className="card-elevated rounded-2xl p-6" data-testid="ms-status-card">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className={`w-11 h-11 rounded-lg grid place-items-center border ${settings?.connected ? "bg-emerald-500/10 border-emerald-500/30" : "bg-slate-500/10 border-white/10"}`}>
              {settings?.connected ? <CheckCircle2 className="w-5 h-5 text-emerald-400" /> : <XCircle className="w-5 h-5 text-slate-400" />}
            </div>
            <div>
              <div className="font-semibold">
                {settings?.connected ? "Microsoft 365 Bağlı" : settings?.configured ? "Yapılandırıldı · Bağlantı Bekliyor" : "Bağlı Değil"}
              </div>
              {settings?.connected_email && (
                <div className="text-sm text-slate-400 mono">{settings.connected_email}</div>
              )}
              {!settings?.connected && settings?.configured && (
                <div className="text-sm text-slate-400">Bağlanmadıkça mailler <span className="text-amber-300">Aves Otomasyon şablonu (Resend)</span> ile gönderilir.</div>
              )}
              {!settings?.configured && (
                <div className="text-sm text-slate-400">Azure App Registration bilgilerinizi girin.</div>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            {settings?.connected && (
              <>
                <Button onClick={testSend} disabled={busy} variant="outline" className="border-white/10" data-testid="test-send-btn">
                  <Send className="w-4 h-4 mr-1" /> Test Maili Gönder
                </Button>
                <Button onClick={disconnect} variant="outline" className="border-red-500/30 text-red-300 hover:bg-red-500/10" data-testid="disconnect-btn">
                  Bağlantıyı Kaldır
                </Button>
              </>
            )}
            {!settings?.connected && settings?.configured && (
              <Button onClick={connect} disabled={busy} className="bg-amber-500 hover:bg-amber-400 text-black font-semibold" data-testid="connect-ms-btn">
                <LinkIcon className="w-4 h-4 mr-1" /> Microsoft ile Bağlan
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Setup instructions */}
      <div className="card-elevated rounded-2xl p-6" data-testid="ms-instructions">
        <div className="flex items-center gap-2 mb-3">
          <Shield className="w-4 h-4 text-amber-400" />
          <h3 className="font-semibold">Kurulum Adımları (Azure Portal)</h3>
        </div>
        <ol className="space-y-2.5 text-sm text-slate-300 list-decimal list-inside marker:text-amber-400">
          <li>
            <a href="https://entra.microsoft.com" target="_blank" rel="noreferrer" className="text-amber-300 hover:underline inline-flex items-center gap-1">
              entra.microsoft.com <ExternalLink className="w-3 h-3" />
            </a> adresine yönetici hesabıyla girin.
          </li>
          <li>Sol menüden <strong>Applications → App registrations → New registration</strong>.</li>
          <li>Name: <span className="mono text-amber-300">Aves Elektrik Cari Takip</span> · Account types: <em>Single tenant</em>.</li>
          <li>
            Redirect URI (Web) alanına aşağıdaki adresi ekleyin ve <strong>Register</strong>:
            <div className="mt-2 flex items-center gap-2 rounded-lg border border-white/10 bg-[#111723] p-2 mono text-xs">
              <span className="flex-1 truncate">{suggestedRedirect}</span>
              <Button size="sm" variant="ghost" onClick={() => copy(suggestedRedirect)}>
                <Copy className="w-3.5 h-3.5" />
              </Button>
            </div>
          </li>
          <li>Application (client) ID ve Directory (tenant) ID değerlerini kopyalayın.</li>
          <li><strong>Certificates & secrets → New client secret</strong> · 12 ay · <em>Value</em>'ı hemen kopyalayın (bir daha görünmez).</li>
          <li>
            <strong>API permissions → Add a permission → Microsoft Graph → Delegated permissions</strong> ·
            <span className="mono text-amber-300"> Mail.Send</span> + <span className="mono text-amber-300">offline_access</span> + <span className="mono text-amber-300">User.Read</span> → <em>Add permissions</em> → <em>Grant admin consent</em>.
          </li>
          <li>Aşağıdaki forma değerleri yapıştırıp <strong>Kaydet</strong>, sonra <strong>Microsoft ile Bağlan</strong>.</li>
        </ol>
      </div>

      {/* Configuration form */}
      <div className="card-elevated rounded-2xl p-6" data-testid="ms-config-form">
        <div className="flex items-center gap-2 mb-4">
          <Link2 className="w-4 h-4 text-amber-400" />
          <h3 className="font-semibold">Azure App Registration Bilgileri</h3>
          {settings?.has_secret && <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/25">Secret Kayıtlı</Badge>}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Application (client) ID *</Label>
            <Input value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} placeholder="00000000-0000-0000-0000-000000000000" className="bg-[#111723] border-white/10 mt-1 mono text-sm" data-testid="ms-client-id" />
          </div>
          <div>
            <Label>Directory (tenant) ID *</Label>
            <Input value={form.tenant_id} onChange={(e) => setForm({ ...form, tenant_id: e.target.value })} placeholder="00000000-0000-0000-0000-000000000000" className="bg-[#111723] border-white/10 mt-1 mono text-sm" data-testid="ms-tenant-id" />
          </div>
          <div className="md:col-span-2">
            <Label>Client Secret {settings?.has_secret ? "(değiştirmek için doldurun)" : "*"}</Label>
            <Input type="password" value={form.client_secret} onChange={(e) => setForm({ ...form, client_secret: e.target.value })} placeholder={settings?.has_secret ? "••••••• (kayıtlı)" : "Secret Value"} className="bg-[#111723] border-white/10 mt-1 mono text-sm" data-testid="ms-client-secret" />
            <div className="text-[11px] text-slate-500 mt-1">Şifrelenmiş olarak veritabanında saklanır. Kaynak kodunda tutulmaz.</div>
          </div>
          <div>
            <Label>Gönderici Firma E-posta *</Label>
            <Input type="email" value={form.from_email} onChange={(e) => setForm({ ...form, from_email: e.target.value })} placeholder="info@aveselektrik.com" className="bg-[#111723] border-white/10 mt-1" data-testid="ms-from-email" />
            <div className="text-[11px] text-slate-500 mt-1">Bağlanılacak hesap ile aynı olmalı.</div>
          </div>
          <div>
            <Label>Redirect URI (opsiyonel)</Label>
            <Input value={form.redirect_uri} onChange={(e) => setForm({ ...form, redirect_uri: e.target.value })} placeholder={suggestedRedirect} className="bg-[#111723] border-white/10 mt-1 mono text-sm" data-testid="ms-redirect-uri" />
            <div className="text-[11px] text-slate-500 mt-1">Boş bırakırsanız otomatik oluşturulur.</div>
          </div>
        </div>
        <div className="mt-5 flex justify-end">
          <Button onClick={save} disabled={busy} className="bg-amber-500 hover:bg-amber-400 text-black font-semibold" data-testid="save-ms-settings-btn">
            {busy ? "Kaydediliyor..." : "Kaydet"}
          </Button>
        </div>
      </div>

      {/* Security notice */}
      <div className="rounded-2xl border border-amber-500/25 bg-amber-500/5 p-5 text-sm text-slate-300 leading-relaxed">
        <div className="flex items-center gap-2 text-amber-300 font-semibold mb-2">
          <Shield className="w-4 h-4" /> Güvenlik
        </div>
        Microsoft şifreniz hiçbir zaman uygulama tarafından istenmez veya saklanmaz. Bağlantı OAuth 2.0 yetkilendirme akışı ile yapılır; sadece <strong>Mail.Send</strong>, <strong>User.Read</strong> ve <strong>offline_access</strong> izinleri talep edilir. Refresh token AES şifreli olarak veritabanında tutulur.
      </div>
    </div>
  );
}
